import React from 'react';
import { motion } from 'framer-motion';
import { Activity } from '../../types/activities';
import { ActivityCard } from './ActivityCard';
import { CalendarX2, Sparkles } from 'lucide-react';

interface ActivityListProps {
  activities: Activity[];
  isLoading: boolean;
  currentUserId?: string;
  onOpenActivity: (activity: Activity) => void;
  onLike?: (activityId: string) => void;
  onFavorite?: (activityId: string) => void;
  likedIds?: Set<string>;
  favoritedIds?: Set<string>;
}

function SkeletonCard() {
  return (
    <div className="rounded-2xl bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 overflow-hidden shadow-sm">
      <div className="h-44 sm:h-48 bg-gray-100 dark:bg-gray-700 animate-pulse" />
      <div className="p-4 space-y-3">
        <div className="h-5 w-3/4 bg-gray-100 dark:bg-gray-700 rounded-lg animate-pulse" />
        <div className="space-y-2">
          <div className="h-3 bg-gray-100 dark:bg-gray-700 rounded w-full animate-pulse" />
          <div className="h-3 bg-gray-100 dark:bg-gray-700 rounded w-2/3 animate-pulse" />
        </div>
        <div className="flex items-center gap-3">
          <div className="h-3.5 bg-gray-100 dark:bg-gray-700 rounded w-24 animate-pulse" />
          <div className="h-3.5 bg-gray-100 dark:bg-gray-700 rounded w-16 animate-pulse" />
        </div>
        <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-gray-700/50">
          <div className="h-4 bg-gray-100 dark:bg-gray-700 rounded w-20 animate-pulse" />
          <div className="h-4 bg-gray-100 dark:bg-gray-700 rounded w-16 animate-pulse" />
        </div>
      </div>
    </div>
  );
}

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.06, duration: 0.4, ease: 'easeOut' },
  }),
};

export function ActivityList({
  activities, isLoading, currentUserId, onOpenActivity,
  onLike, onFavorite, likedIds, favoritedIds,
}: ActivityListProps) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {[...Array(6)].map((_, i) => (
          <SkeletonCard key={i} />
        ))}
      </div>
    );
  }

  if (activities.length === 0) {
    return (
      <div className="rounded-2xl bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 p-12 text-center shadow-sm">
        <div className="flex flex-col items-center gap-5">
          <div className="relative">
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-teal-50 to-emerald-50 dark:from-teal-900/20 dark:to-emerald-900/20 flex items-center justify-center">
              <CalendarX2 className="w-9 h-9 text-teal-400 dark:text-teal-500" />
            </div>
            <div className="absolute -top-1 -right-1 w-7 h-7 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-amber-500" />
            </div>
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
              Aucune activité pour le moment
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm mx-auto leading-relaxed">
              Soyez le premier à proposer une activité et rencontrez d'autres voyageurs dans votre ville !
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
      {activities.map((activity, i) => (
        <motion.div
          key={activity.id}
          custom={i}
          variants={cardVariants}
          initial="hidden"
          animate="visible"
        >
          <ActivityCard
            activity={activity}
            onClick={() => onOpenActivity(activity)}
            isOwn={currentUserId === activity.userId}
            isLiked={likedIds?.has(activity.id)}
            isFavorited={favoritedIds?.has(activity.id)}
            onLike={() => onLike?.(activity.id)}
            onFavorite={() => onFavorite?.(activity.id)}
          />
        </motion.div>
      ))}
    </div>
  );
}
